import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { profileApi, bulkProfileApi, activityApi } from '@/services/profileApi';
import { useNavigate, useLocation } from 'react-router-dom';
import type {
  UserProfileUpdateRequest,
  BioUpdateRequest,
  PreferencesUpdateRequest,
  GdprComplianceUpdateRequest,
  TermsAcceptanceUpdateRequest,
  UserConsentUpdateRequest,
  BulkUpdateRequest,
  ActivityRecord,
} from '@/types/userProfile';
import { useToast } from './useToast';

// Query Keys
export const profileKeys = {
  all: ['profiles'] as const,
  lists: () => [...profileKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...profileKeys.lists(), filters] as const,
  details: () => [...profileKeys.all, 'detail'] as const,
  detail: (id: string) => [...profileKeys.details(), id] as const,
  analytics: () => [...profileKeys.all, 'analytics'] as const,
  activity: (userId: string, period?: string) => [...profileKeys.all, 'activity', userId, period] as const,
};

// Get Profile by ID
export const useProfile = (userId: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: profileKeys.detail(userId || ''),
    queryFn: () => profileApi.getById(userId!),
    enabled: enabled && !!userId,
  });
};

// Get Profile by Email
export const useProfileByEmail = (email: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: [...profileKeys.details(), 'email', email],
    queryFn: () => profileApi.getByEmail(email!),
    enabled: enabled && !!email,
  });
};

// Get Profile by Username
export const useProfileByUsername = (userName: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: [...profileKeys.details(), 'username', userName],
    queryFn: () => profileApi.getByUsername(userName!),
    enabled: enabled && !!userName,
  });
};

// Get Profile by Username or ID (backward compatibility)
export const useProfileByUsernameOrId = (identifier: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: [...profileKeys.details(), 'identifier', identifier],
    queryFn: () => profileApi.getByUsernameOrId(identifier!),
    enabled: enabled && !!identifier,
  });
};

// List Profiles
export const useProfiles = (params?: {
  page?: number;
  size?: number;
  sort?: string;
}) => {
  return useQuery({
    queryKey: profileKeys.list(params),
    queryFn: () => profileApi.list(params),
  });
};

// Get Profiles by Completion
export const useProfilesByCompletion = (
  completed: boolean,
  params?: { page?: number; size?: number }
) => {
  return useQuery({
    queryKey: [...profileKeys.lists(), 'completion', completed, params],
    queryFn: () => profileApi.getByCompletion(completed, params),
  });
};

// Search Profiles by Skills
export const useProfilesBySkills = (
  skillIds: string[],
  params?: { page?: number; size?: number }
) => {
  return useQuery({
    queryKey: [...profileKeys.lists(), 'skills', skillIds, params],
    queryFn: () => profileApi.searchBySkills(skillIds, params),
    enabled: skillIds.length > 0,
  });
};

// Search Profiles by Interests
export const useProfilesByInterests = (
  interests: string[],
  params?: { page?: number; size?: number }
) => {
  return useQuery({
    queryKey: [...profileKeys.lists(), 'interests', interests, params],
    queryFn: () => profileApi.searchByInterests(interests, params),
    enabled: interests.length > 0,
  });
};

// Get Profiles by Location
export const useProfilesByLocation = (params: {
  country?: string;
  state?: string;
  city?: string;
  page?: number;
  size?: number;
}) => {
  return useQuery({
    queryKey: [...profileKeys.lists(), 'location', params],
    queryFn: () => profileApi.getByLocation(params),
  });
};

// Get Inactive Users
export const useInactiveUsers = (
  days: number,
  params?: { page?: number; size?: number }
) => {
  return useQuery({
    queryKey: [...profileKeys.lists(), 'inactive', days, params],
    queryFn: () => profileApi.getInactive(days, params),
  });
};

// Get Analytics
export const useProfileAnalytics = () => {
  return useQuery({
    queryKey: profileKeys.analytics(),
    queryFn: () => profileApi.getAnalytics(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get Activity Summary
export const useActivitySummary = (
  userId: string,
  period: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR' = 'WEEK'
) => {
  return useQuery({
    queryKey: profileKeys.activity(userId, period),
    queryFn: () => activityApi.getActivitySummary(userId, period),
    enabled: !!userId,
  });
};

// Get Profile Completion Details
export const useProfileCompletionDetails = (userId: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: [...profileKeys.details(), userId, 'completion-details'],
    queryFn: () => profileApi.getCompletionDetails(userId!),
    enabled: enabled && !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Mutations

// Update Profile
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const maybeRedirectToCheckout = (message: string | undefined) => {
    const msg = String(message ?? '').toLowerCase();
    // Backend may respond with an "invalid input" message indicating payment selection is required.
    const looksLikePaymentMissing = msg.includes('payment') || msg.includes('checkout');
    if (!looksLikePaymentMissing) return false;
    if (location.pathname.includes('/checkout')) return true;
    navigate('/checkout', { replace: true });
    return true;
  };

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UserProfileUpdateRequest }) =>
      profileApi.update(userId, data),
    onSuccess: (_data, variables) => {
      const userId = variables.userId;
      // Completion recalculation happens in the backend as part of profile update,
      // but we must invalidate all possible "completion-details" query keys
      // used across the portal UI.
      queryClient.invalidateQueries({ queryKey: ['profile-completion-details', userId] });
      queryClient.invalidateQueries({ queryKey: [...profileKeys.details(), userId, 'completion-details'] });
      queryClient.invalidateQueries({ queryKey: profileKeys.details() });
      queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
      toast({
        variant: 'default',
        title: 'Success',
        description: 'Profile updated successfully',
      });
    },
    onError: (error: Error) => {
      maybeRedirectToCheckout(error?.message);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to update profile',
      });
    },
  });
};

// Update Bio
export const useUpdateBio = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const maybeRedirectToCheckout = (message: string | undefined) => {
    const msg = String(message ?? '').toLowerCase();
    const looksLikePaymentMissing = msg.includes('payment') || msg.includes('checkout');
    if (!looksLikePaymentMissing) return false;
    if (location.pathname.includes('/checkout')) return true;
    navigate('/checkout', { replace: true });
    return true;
  };

  return useMutation({
    mutationFn: ({ userId, bio }: { userId: string; bio: BioUpdateRequest }) =>
      profileApi.updateBio(userId, bio),
    onSuccess: (_data, variables) => {
      const userId = variables.userId;
      queryClient.invalidateQueries({ queryKey: ['profile-completion-details', userId] });
      queryClient.invalidateQueries({ queryKey: [...profileKeys.details(), userId, 'completion-details'] });
      queryClient.invalidateQueries({ queryKey: profileKeys.details() });
      toast({
        variant: 'default',
        title: 'Success',
        description: 'Bio updated successfully',
      });
    },
    onError: (error: Error) => {
      maybeRedirectToCheckout(error?.message);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to update bio',
      });
    },
  });
};

// Generate Summary
export const useGenerateSummary = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const maybeRedirectToCheckout = (message: string | undefined) => {
    const msg = String(message ?? '').toLowerCase();
    const looksLikePaymentMissing = msg.includes('payment') || msg.includes('checkout');
    if (!looksLikePaymentMissing) return false;
    if (location.pathname.includes('/checkout')) return true;
    navigate('/checkout', { replace: true });
    return true;
  };

  return useMutation({
    mutationFn: (userId: string) => profileApi.generateSummary(userId),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.detail(userId) });
      toast({
        variant: 'default',
        title: 'Success',
        description: 'AI summary generated successfully',
      });
    },
    onError: (error: Error) => {
      maybeRedirectToCheckout(error?.message);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to generate summary',
      });
    },
  });
};

// Update Completion
export const useUpdateCompletion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => profileApi.updateCompletion(userId),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.detail(userId) });
      toast({
        variant: 'default',
        title: 'Success',
        description: 'Completion percentage updated',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to update completion',
      });
    },
  });
};

// Update Last Active
export const useUpdateLastActive = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => profileApi.updateLastActive(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.detail(userId) });
      toast({
        variant: 'default',
        title: 'Success',
        description: 'Last active updated',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to update last active',
      });
    },
  });
};

// Delete Profile
export const useDeleteProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => profileApi.delete(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
      queryClient.removeQueries({ queryKey: profileKeys.detail(userId) });
      toast({
        variant: 'default',
        title: 'Success',
        description: 'Profile deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to delete profile',
      });
    },
  });
};

// Update Preferences
export const useUpdatePreferences = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ userId, preferences }: { userId: string; preferences: PreferencesUpdateRequest }) =>
      profileApi.updatePreferences(userId, preferences),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.detail(variables.userId) });
      toast({
        variant: 'default',
        title: 'Success',
        description: 'Preferences updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to update preferences',
      });
    },
  });
};

// Update GDPR Compliance
export const useUpdateGdprCompliance = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ userId, compliance }: { userId: string; compliance: GdprComplianceUpdateRequest }) =>
      profileApi.updateGdprCompliance(userId, compliance),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.detail(variables.userId) });
      toast({
        variant: 'default',
        title: 'Success',
        description: 'GDPR compliance updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to update GDPR compliance',
      });
    },
  });
};

// Update Terms Acceptance
export const useUpdateTermsAcceptance = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ userId, terms }: { userId: string; terms: TermsAcceptanceUpdateRequest }) =>
      profileApi.updateTermsAcceptance(userId, terms),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.detail(variables.userId) });
      toast({
        variant: 'default',
        title: 'Success',
        description: 'Terms acceptance updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to update terms acceptance',
      });
    },
  });
};

// Update User Consent
export const useUpdateUserConsent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ userId, consent }: { userId: string; consent: UserConsentUpdateRequest }) =>
      profileApi.updateUserConsent(userId, consent),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.detail(variables.userId) });
      toast({
        variant: 'default',
        title: 'Success',
        description: 'User consent updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to update user consent',
      });
    },
  });
};

// Bulk Update
export const useBulkUpdateProfiles = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (request: BulkUpdateRequest) => bulkProfileApi.bulkUpdate(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
      toast({
        variant: 'default',
        title: 'Bulk Update Complete',
        description: `Successfully updated ${data.successCount} of ${data.totalRequested} profiles`,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to perform bulk update',
      });
    },
  });
};

// Track Activity
export const useTrackActivity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (activity: ActivityRecord) => activityApi.track(activity),
    onSuccess: (_, activity) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.activity(activity.userId) });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.message ?? 'Failed to track activity',
      });
    },
  });
};







